import { Component, Inject } from "@angular/core";
import { CommonModule, DecimalPipe } from "@angular/common";
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatTableModule } from "@angular/material/table";
import { MatChipsModule } from "@angular/material/chips";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { MatTooltipModule } from "@angular/material/tooltip";
import {
  OrderAttributes,
  OrderDetailAttributes,
} from "@albatrosdeveloper/ave-models-package";
import { MatTabsModule } from "@angular/material/tabs";
import { getCode } from "app/utils/method";
import { isDate, isNumber } from "lodash";
import { OrderCustomerLogService } from "../order-customer-log.service";
import { IPaginationResult, IRequestResult } from "app/common/interfaces";
import { MatSnackBar } from "@angular/material/snack-bar";
import { FormControl } from "@angular/forms";
import { MatPaginatorModule, PageEvent } from "@angular/material/paginator";
import { DecimalFormatPipe } from "app/pipes/decimal-company.pipe";
import { IFilters } from "app/modules/components/filters/interfaces/filterInterface";
import { DocumentTypeEnum } from "app/common/enums/document-types.enum";
import { UserLoginInfo } from "app/modules/components/monitor/monitor-routes/interfaces/work-progress-material.interface";

interface DialogData {
  before?: OrderAttributes;
  after?: OrderAttributes;
  updatedPositions?: number[];
  serial?: string;
  position?: number; // Nuevo caso: solo el serial
}

interface HistoryItem {
  documentAfterUpdate: OrderAttributes;
  documentBeforeUpdate?: OrderAttributes;
  detailsPositionsUpdated: number[];
  version: number;
  updatedAt: string;
  updatedBy: string;
  createdBySession: UserLoginInfo;
  updatedBySession: UserLoginInfo;
}

@Component({
  selector: "app-order-log-modal",
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatIconModule,
    MatTableModule,
    MatChipsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatTooltipModule,
    MatTabsModule,
    MatPaginatorModule,
    DecimalFormatPipe,
  ],
  templateUrl: "./order-customer-log-modal.component.html",
  //   styleUrls: ['./order-customer-log-modal.component.scss']
  providers: [DecimalPipe],
})
export class OrderLogModalComponent {
  getCode = getCode;
  displayedColumns: string[] = ["field", "before", "after"];
  detailColumns: string[] = [
    "position",
    "material",
    "quantityBefore",
    "quantityAfter", // Cantidad (antes/después)
    "quantityConfirmedBefore",
    "quantityConfirmedAfter",
    "priceBefore",
    "priceAfter", // Precio (antes/después)
  ];

  comparisonData: {
    before: OrderAttributes;
    after: OrderAttributes;
    updatedPositions: number[];
    createdBySession: UserLoginInfo;
    updatedBySession: UserLoginInfo;
  } | null = null;

  selectedVersion1: number | null = null;
  selectedVersion2: number | null = null;

  // Paginación
  totalItems: number = 0;
  searchControl = new FormControl("");
  search: string = "";
  pageSize: number = 50;
  pageIndex: number = 1;
  sortActive: keyof OrderDetailAttributes = "total";
  sortDirection: "ASC" | "DESC" = "DESC";

  filter = {
    company: null,
    dateini: new Date(),
    dateend: new Date(),
    warehouseId: null,
    routes: [],
    item: null,
  };

  historyItems: any[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private orderLogService: OrderCustomerLogService,
    private snackBar: MatSnackBar,
    private dialogRef: MatDialogRef<OrderLogModalComponent>
  ) {}

  ngOnInit(): void {
    if (this.data.serial) {
      // Caso nuevo: cargar datos por servicio
      this.loadOrderHistory();
    } else if (this.data.before && this.data.after) {
      // Caso actual: datos directos
      this.comparisonData = {
        before: this.data.before,
        after: this.data.after,
        updatedPositions: this.data.updatedPositions || [],
        createdBySession: null,
        updatedBySession: null
      };
    }
  }

  loadOrderHistory() {
    let options = {
      currentPage: this.pageIndex,
      limitPerPage: this.pageSize,
      search: this.search,
      sortDirection: this.sortDirection,
    };

    let filter: IFilters = {
      type: DocumentTypeEnum.PEDIDO, //Factura,
      serial: this.data.serial,
      // position: this.data.position, // Nueva propiedad para la posición
    };
    this.orderLogService.findAllWithCount(options, filter).subscribe({
      next: (res: IPaginationResult<OrderDetailAttributes>) => {
        const sortedItems = (res?.data?.[0]?.data ?? [])
          .map((item) => ({
            ...item,
            updatedAt: new Date(item.updatedAt), // Asegurar que es Date
          }))
          .sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());

        // Asignar números de versión correctos (la más reciente = mayor número)
        this.historyItems = sortedItems.map((item, index) => ({
          ...item,
          version: sortedItems.length - index, // La más reciente tendrá el número más alto
        }));
        this.totalItems = res?.data?.[0]?.pageInfo?.total ?? 0;
        // Si hay al menos 2 versiones, comparamos las dos más recientes
        if (this.historyItems.length >= 2) {
          this.compareVersions(this.historyItems[1], this.historyItems[0]);
        } else if (this.historyItems.length === 1) {
        this.compareVersions(this.historyItems[0]);
        }
      },
      error: (err) => {
        this.snackBar.open(
          err.error.message || "Error al obtener reporte",
          "Cerrar",
          { duration: 5000 }
        );
      },
    });
  }

  // compareVersions(previousVersion: HistoryItem, currentVersion: HistoryItem) {
  //     this.selectedVersion1 = previousVersion.version;
  //     this.selectedVersion2 = currentVersion.version;

  //     this.comparisonData = {
  //         before: previousVersion.documentAfterUpdate,
  //         after: currentVersion.documentAfterUpdate,
  //         updatedPositions: this.calculateUpdatedPositions(
  //             previousVersion.documentAfterUpdate,
  //             currentVersion.documentAfterUpdate,
  //             currentVersion.detailsPositionsUpdated
  //         )
  //     };
  // }

  compareVersions(previousOrSingle: HistoryItem, current?: HistoryItem) {
    const isSingle = !current; // solo vino 1 registro (con before/after internos)

    const before: OrderAttributes = isSingle
      ? previousOrSingle.documentBeforeUpdate ?? ({} as OrderAttributes)
      : previousOrSingle.documentAfterUpdate ?? ({} as OrderAttributes);

    const after: OrderAttributes = isSingle
      ? previousOrSingle.documentAfterUpdate ?? ({} as OrderAttributes)
      : current!.documentAfterUpdate ?? ({} as OrderAttributes);

    const explicitUpdatedPositions = isSingle
      ? previousOrSingle.detailsPositionsUpdated
      : current!.detailsPositionsUpdated;

    // Manejo de “versiones” para UI: si es single, puedes mostrar una “previa virtual”
    this.selectedVersion1 = isSingle
      ? (previousOrSingle.version ?? 1) - 1 // virtual (previa)
      : previousOrSingle.version;

    this.selectedVersion2 = isSingle
      ? previousOrSingle.version
      : current!.version;

    this.comparisonData = {
      before,
      after,
      updatedPositions: this.calculateUpdatedPositions(
        before,
        after,
        explicitUpdatedPositions
      ),
      createdBySession: previousOrSingle.createdBySession,
      updatedBySession: previousOrSingle.updatedBySession
    };
  }

  onVersionChange() {
    if (this.selectedVersion1 && this.selectedVersion2) {
      const version1 = this.historyItems.find(
        (item) => item.version === this.selectedVersion1
      );
      const version2 = this.historyItems.find(
        (item) => item.version === this.selectedVersion2
      );

      if (version1 && version2) {
        this.compareVersions(version1, version2);
      }
    }
  }

  //   private calculateUpdatedPositions(
  //     before: OrderAttributes,
  //     after: OrderAttributes,
  //     explicitUpdatedPositions?: number[]
  //   ): number[] {
  //     // Si tenemos posiciones explícitas, las usamos
  //     if (explicitUpdatedPositions && explicitUpdatedPositions.length > 0) {
  //       return explicitUpdatedPositions;
  //     }

  //     const updatedPositions: number[] = [];

  //     // Si es la primera versión (creación)
  //     if (!before.detail) {
  //       return after.detail?.map((d) => d.position) || [];
  //     }

  //     // Comparar detalles para encontrar cambios
  //     after.detail?.forEach((afterDetail) => {
  //       const beforeDetail = before.detail?.find(
  //         (d) => d.position === afterDetail.position
  //       );

  //       if (
  //         !beforeDetail ||
  //         beforeDetail.soldQuantity !== afterDetail.soldQuantity ||
  //         beforeDetail.unitPrice !== afterDetail.unitPrice ||
  //         beforeDetail.material.externalKeys.code !==
  //           afterDetail.material.externalKeys.code
  //       ) {
  //         updatedPositions.push(afterDetail.position);
  //       }
  //     });

  //     return updatedPositions;
  //   }

  private calculateUpdatedPositions(
    before: OrderAttributes,
    after: OrderAttributes,
    explicitUpdatedPositions?: number[]
  ): number[] {
    // 1) Si vienen explícitas, úsalas tal cual
    if (explicitUpdatedPositions && explicitUpdatedPositions.length > 0) {
      return Array.from(new Set(explicitUpdatedPositions)).sort(
        (a, b) => a - b
      );
    }

    const updated: number[] = [];
    const beforeDetails = before?.detail ?? [];
    const afterDetails = after?.detail ?? [];

    // 2) Si es “creación” (no había detalles antes)
    if (!beforeDetails || beforeDetails.length === 0) {
      return afterDetails.map((d) => d.position).sort((a, b) => a - b);
    }

    // 3) Comparar detalle por detalle por posición
    afterDetails.forEach((afterDetail) => {
      const pos = afterDetail?.position;
      const beforeDetail = beforeDetails.find((d) => d?.position === pos);

      const beforeCode = beforeDetail?.material?.externalKeys?.code;
      const afterCode = afterDetail?.material?.externalKeys?.code;

      if (
        !beforeDetail ||
        beforeDetail?.soldQuantity !== afterDetail?.soldQuantity ||
        beforeDetail?.unitPrice !== afterDetail?.unitPrice ||
        beforeCode !== afterCode
      ) {
        if (typeof pos === "number") updated.push(pos);
      }
    });

    // 4) Quitar duplicados y ordenar
    return Array.from(new Set(updated)).sort((a, b) => a - b);
  }

  getChangedDetails() {
    if (!this.comparisonData) return [];

    // Si tenemos una posición específica, filtramos solo esa
    if (this.data.position) {
      return this.comparisonData.after.detail
        .filter((item) => item.position === this.data.position)
        .filter((item) =>
          this.comparisonData.updatedPositions.includes(item.position)
        );
    }

    return this.comparisonData.after.detail.filter((item) =>
      this.comparisonData.updatedPositions.includes(item.position)
    );
  }

  getFieldChanges(): any[] {
    if (!this.comparisonData) return [];

    const changes = [];
    // Fechas
    changes.push({
      field: "Fecha",
      before:
        this.comparisonData.before.updatedAt ||
        this.comparisonData.before.createdAt,
      after:
        this.comparisonData.after.updatedAt ||
        this.comparisonData.after.createdAt,
      isDate: true,
    });

    // Usuario
    changes.push({
      field: "Modificado por",
      before: this.comparisonData?.createdBySession?.username || "AVE",
      after: this.comparisonData?.updatedBySession?.username || "AVE",
    });

    // Campos numéricos
    if (
      isNumber(this.comparisonData.before.subTotal) ||
      isNumber(this.comparisonData.after.subTotal)
    ) {
      changes.push({
        field: "Subtotal",
        before: this.comparisonData.before.subTotal,
        after: this.comparisonData.after.subTotal,
        isNumber: true,
      });
    }

    if (
      isNumber(this.comparisonData.before.total) ||
      isNumber(this.comparisonData.after.total)
    ) {
      changes.push({
        field: "Total",
        before: this.comparisonData.before.total,
        after: this.comparisonData.after.total,
        isNumber: true,
      });
    }

    return changes;
  }

  findOriginalQuantity(position: number): number | null {
    if (!this.comparisonData) return null;
    const originalDetail = this.comparisonData.before.detail?.find(
      (d) => d.position === position
    );
    return originalDetail ? originalDetail.soldQuantity : null;
  }

  findOriginalPrice(position: number): number | null {
    if (!this.comparisonData) return null;
    const originalDetail = this.comparisonData.before.detail?.find(
      (d) => d.position === position
    );
    return originalDetail ? originalDetail.unitPrice : null;
  }

  findOriginalDetail(position: number): any {
    if (!this.comparisonData) return null;
    return this.comparisonData.before.detail?.find(
      (d) => d.position === position
    );
  }

  onPageChange(event: PageEvent) {
    this.pageIndex = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.loadOrderHistory();
  }
  // getChangedDetails() {
  //     if (!this.comparisonData) return [];
  //     return this.comparisonData.after.detail.filter(item =>
  //         this.comparisonData.updatedPositions.includes(item.position)
  //     );
  // }

  // getFieldChanges(): any[] {
  //     if (!this.comparisonData) return [];

  //     return [
  //         {
  //             field: 'Fecha Modificación',
  //             before: this.comparisonData.before.createdAt,
  //             after: this.comparisonData.after.updatedAt,
  //             isDate: true
  //         },
  //         {
  //             field: 'Subtotal',
  //             before: this.comparisonData.before.subTotal,
  //             after: this.comparisonData.after.subTotal,
  //             isNumber: true
  //         },
  //         {
  //             field: 'Total',
  //             before: this.comparisonData.before.total,
  //             after: this.comparisonData.after.total,
  //             isNumber: true
  //         },
  //     ];
  // }

  // findOriginalQuantity(position: number): number | null {
  //     if (!this.comparisonData) return null;
  //     const originalDetail = this.comparisonData.before.detail.find(d => d.position === position);
  //     return originalDetail ? originalDetail.soldQuantity : null;
  // }

  // findOriginalPrice(position: number): number | null {
  //     if (!this.comparisonData) return null;
  //     const originalDetail = this.comparisonData.before.detail.find(d => d.position === position);
  //     return originalDetail ? originalDetail.unitPrice : null;
  // }

  // findOriginalDetail(position: number): any {
  //     if (!this.comparisonData) return null;
  //     return this.comparisonData.before.detail.find(d => d.position === position);
  // }
}
